/**
 * 軍札（いくさふだ）— 合戦専用のデッキ
 *
 * 兵種（スート）4〜5 種 × 数 1〜13。
 *
 * 第I章は 4 兵種 × 13 = 52 枚。標準的なトランプとまったく同じ構成になるので、
 * 役の出現数も標準ポーカーと **ほぼ** 一致する。ポーカーを知っている人は
 * 説明なしで遊べる、という利点をここで確保している。
 *
 * 「ほぼ」なのは連続の扱いだけが違うため。標準ポーカーは A を上下両用にして
 * ストレートの窓が 10 通りあるが、本作は 1〜13 の一本道で繰り上がらないので 9 通り。
 * その差 1 窓 ×(4^5 − 4) = 1,020 手ぶんだけ雁行が減り、単騎駆に移る。
 * formation.test.ts が両者を突き合わせて固定している。
 *
 * 1543 年の鉄砲伝来で 5 兵種目が加わり 65 枚になる。
 * 同じ数が 5 枚存在するようになるため「無双（同数 5 枚）」が成立し、
 * 方円（フルハウス）が作りやすくなる。時代がデッキを書き換える。
 *
 * 【設計上の注意】ランクを 9 までにすると、ペアが出すぎて
 * 長蛇（1 ペア 51.6%）が単騎駆（役なし 30.9%）より多くなり、
 * 「役なしがペアに勝つ」という破綻が起きる。13 ランクはその実測を受けた値。
 * docs/06-賭けと軍配勝負.md §16
 */

export type Arm = 'ashigaru' | 'yumi' | 'kiba' | 'souhei' | 'teppou';

/** 兵種の並び。CardId のエンコードに使うので順序を変えないこと。 */
export const ARMS: readonly Arm[] = ['ashigaru', 'yumi', 'kiba', 'souhei', 'teppou'];

export const ARM_LABEL: Readonly<Record<Arm, string>> = {
  ashigaru: '足軽',
  yumi: '弓',
  kiba: '騎馬',
  souhei: '僧兵',
  teppou: '鉄砲',
};

export const RANK_MIN = 1;
export const RANK_MAX = 13;
export const RANK_COUNT = RANK_MAX - RANK_MIN + 1;

/** 連続（雁行・鋒矢）が成立する窓の数。1-5 … 9-13 の 9 通り。繰り上がりなし。 */
export const STRAIGHT_WINDOWS = RANK_COUNT - 4;

/**
 * 札を 1 個の整数で表す（0..64）。
 *   id = 兵種番号 * 13 + (数 - 1)
 * 判定を配列演算で回すため。個別の ID 文字列は持たない。
 */
export type CardId = number;

export function cardId(arm: Arm, rank: number): CardId {
  const armIndex = ARMS.indexOf(arm);
  if (armIndex < 0) throw new RangeError(`unknown arm: ${arm}`);
  if (rank < RANK_MIN || rank > RANK_MAX) throw new RangeError(`rank out of range: ${rank}`);
  return armIndex * RANK_COUNT + (rank - RANK_MIN);
}

export const armIndexOf = (id: CardId): number => Math.floor(id / RANK_COUNT);
export const armOf = (id: CardId): Arm => ARMS[armIndexOf(id)]!;
export const rankOf = (id: CardId): number => (id % RANK_COUNT) + RANK_MIN;

export const cardLabel = (id: CardId): string => `${ARM_LABEL[armOf(id)]}${rankOf(id)}`;

// ---------------------------------------------------------------------------
// 章ごとのデッキ
// ---------------------------------------------------------------------------

/**
 * 山札の構成。史実イベントで書き換わるのが本作の要点：
 *   第 I 章       52 枚（鉄砲はまだ存在しない。標準トランプと同構成）
 *   1543 鉄砲伝来 → 65 枚
 *   1588 刀狩     → 僧兵が半減して 59 枚
 *
 * docs/06 §2「デッキが時代とともに変わる」
 */
export interface DeckSpec {
  /** 含まれる兵種と、その兵種で使うランクの上限 */
  readonly arms: ReadonlyArray<{ readonly arm: Arm; readonly maxRank: number }>;
}

const FULL = (arm: Arm) => ({ arm, maxRank: RANK_MAX });

/** 刀狩後の僧兵：13 枚 → 7 枚（ランク 1〜7 のみ残る） */
const HALVED_SOUHEI = { arm: 'souhei' as const, maxRank: Math.ceil(RANK_COUNT / 2) };

export const DECK_CHAPTER_1: DeckSpec = {
  arms: [FULL('ashigaru'), FULL('yumi'), FULL('kiba'), FULL('souhei')],
};

/** 鉄砲伝来（1543）以降 */
export const DECK_CHAPTER_2: DeckSpec = {
  arms: [FULL('ashigaru'), FULL('yumi'), FULL('kiba'), FULL('souhei'), FULL('teppou')],
};

export const DECK_CHAPTER_3: DeckSpec = DECK_CHAPTER_2;

/** 刀狩（1588）以降 */
export const DECK_CHAPTER_4: DeckSpec = {
  arms: [FULL('ashigaru'), FULL('yumi'), FULL('kiba'), HALVED_SOUHEI, FULL('teppou')],
};

/**
 * 章ごとの既定のデッキ。**これは差し替え可能な既定値であって、正典ではない。**
 *
 * 盤面と同じく、実際に使う構成は `Ruleset.decks` として注入する
 * （`@tenka/data` の `DECKS`）。どの兵種がいつ現れるかは
 * 鉄砲伝来・刀狩という史実の話であって、ルールの話ではないため。
 *
 * 一方、札の符号化（`cardId` / `RANK_MAX`）と役の判定は core に置く。
 * これを差し替え可能にすると、全数列挙で確定させた役の順位（`docs/06 §16`）が
 * データ側の編集で静かに壊れる。**変えてよいものと、変えてはいけないものを
 * パッケージ境界で分けている。**
 */
export const DEFAULT_DECKS: readonly DeckSpec[] = [
  DECK_CHAPTER_1,
  DECK_CHAPTER_2,
  DECK_CHAPTER_3,
  DECK_CHAPTER_4,
];

/** 章（1始まり）→ デッキ構成。範囲外は両端に丸める */
export function deckSpecForChapter(
  chapter: number,
  decks: readonly DeckSpec[] = DEFAULT_DECKS,
): DeckSpec {
  const index = Math.min(Math.max(chapter, 1), decks.length) - 1;
  return decks[index] ?? DECK_CHAPTER_1;
}

export function buildDeck(spec: DeckSpec): CardId[] {
  const deck: CardId[] = [];
  for (const { arm, maxRank } of spec.arms) {
    for (let rank = RANK_MIN; rank <= maxRank; rank++) deck.push(cardId(arm, rank));
  }
  return deck;
}

export const deckForChapter = (chapter: number, decks?: readonly DeckSpec[]): CardId[] =>
  buildDeck(deckSpecForChapter(chapter, decks));
